import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash, Plus, PencilSimple } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

const roleColor = { admin: "bg-[#111] text-white", agent: "bg-[#0047FF] text-white", writer: "bg-[#00C853] text-white" };
const teamColor = { "Primary Team": "bg-[#0047FF] text-white", "Secondary Team": "bg-[#FFAB00] text-black", "Core Team": "bg-[#7c3aed] text-white" };
const TEAMS = ["Primary Team", "Secondary Team", "Core Team"];

export default function Users() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent", team: "" });
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ name: "", team: "", email: "", password: "" });

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const [online, setOnline] = useState([]);
  useEffect(() => {
    const fetchOnline = () => api.get("/active-users").then((r) => setOnline(r.data)).catch(() => {});
    fetchOnline();
    const id = setInterval(fetchOnline, 30000);
    return () => clearInterval(id);
  }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("Team member added");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "agent", team: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const startEdit = (u) => { setEditUser(u); setEditForm({ name: u.name || "", team: u.team || "", email: u.email || "", password: "" }); };
  const saveEdit = async () => {
    try {
      const payload = { name: editForm.name };
      if (editUser.role === "writer" && editForm.team) payload.team = editForm.team;
      if (editForm.email && editForm.email !== editUser.email) payload.email = editForm.email;
      if (editForm.password) payload.password = editForm.password;
      await api.patch(`/users/${editUser.id}`, payload);
      toast.success("Updated");
      setEditUser(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const UserRow = ({ u }) => (
    <div className="flex items-center justify-between px-5 py-3" data-testid={`user-row-${u.email}`}>
      <div>
        <p className="font-medium text-sm">{u.name}</p>
        <p className="font-mono-data text-xs text-[#555]">{u.email}</p>
      </div>
      <div className="flex items-center gap-3">
        {u.role === "writer" && u.team && <Badge className={`rounded-none ${teamColor[u.team] || "bg-[#999] text-white"}`}>{u.team}</Badge>}
        <Badge className={`rounded-none ${roleColor[u.role]}`}>{u.role}</Badge>
        {u.role !== "admin" && (
          <button onClick={() => startEdit(u)} className="text-[#555] hover:text-[#0047FF] transition-colors" data-testid={`edit-user-${u.email}`}>
            <PencilSimple size={15} weight="bold" />
          </button>
        )}
        {u.role !== "admin" && (
          <button onClick={() => remove(u.id)} className="text-[#e11d48] hover:opacity-70 transition-opacity" data-testid={`delete-user-${u.email}`}>
            <Trash size={16} weight="bold" />
          </button>
        )}
      </div>
    </div>
  );

  const groups = { agent: users.filter((u) => u.role === "agent"), writer: users.filter((u) => u.role === "writer"), admin: users.filter((u) => u.role === "admin") };

  return (
    <div className="p-6 md:p-8">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="font-mono text-xs tracking-widest text-[#0047FF]">MANAGEMENT</p>
          <h2 className="font-heading text-4xl font-bold tracking-tighter">Team</h2>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none bg-[#0047FF] hover:bg-[#0035c4] text-white transition-colors" data-testid="add-user-button">
              <Plus weight="bold" className="mr-2" size={16} /> Add member
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none border-[#111]">
            <DialogHeader><DialogTitle className="font-heading tracking-tight">New team member</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="text-xs font-mono uppercase">Name</Label>
                <Input className="rounded-none border-[#111] mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" /></div>
              <div><Label className="text-xs font-mono uppercase">Email</Label>
                <Input type="email" className="rounded-none border-[#111] mt-1" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" /></div>
              <div><Label className="text-xs font-mono uppercase">Password</Label>
                <Input className="rounded-none border-[#111] mt-1" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password-input" /></div>
              <div><Label className="text-xs font-mono uppercase">Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="rounded-none border-[#111] mt-1" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none">
                    <SelectItem value="agent">Agent</SelectItem>
                    <SelectItem value="writer">Writer</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select></div>
              {form.role === "writer" && (
                <div><Label className="text-xs font-mono uppercase">Team</Label>
                  <Select value={form.team || ""} onValueChange={(v) => setForm({ ...form, team: v })}>
                    <SelectTrigger className="rounded-none border-[#111] mt-1" data-testid="user-team-select"><SelectValue placeholder="Assign team" /></SelectTrigger>
                    <SelectContent className="rounded-none">{TEAMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select></div>
              )}
            </div>
            <DialogFooter>
              <Button onClick={create} className="rounded-none bg-[#0047FF] hover:bg-[#0035c4] text-white" data-testid="save-user-button">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mb-8">
        <h3 className="font-heading text-lg font-bold tracking-tight mb-3 flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-full bg-[#00C853] animate-pulse" /> Currently Online ({online.length})
        </h3>
        <div className="bg-white border border-[#111] divide-y divide-[#e5e5e5]" data-testid="online-users">
          {online.length === 0 && <p className="px-5 py-3 text-xs text-[#999] font-mono">No one active in the last 5 minutes.</p>}
          {online.map((u) => (
            <div key={u.id} className="flex items-center justify-between px-5 py-3" data-testid={`online-${u.email}`}>
              <div>
                <p className="font-medium text-sm">{u.name}</p>
                <p className="font-mono-data text-xs text-[#555]">{u.email}</p>
              </div>
              <div className="flex items-center gap-3">
                <Badge className={`rounded-none ${roleColor[u.role]}`}>{u.role}</Badge>
                <span className="font-mono-data text-[11px] text-[#00C853]">active {new Date(u.last_seen).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {["agent", "admin"].map((role) => (
        <div key={role} className="mb-8">
          <h3 className="font-heading text-lg font-bold tracking-tight mb-3 capitalize">{role}s ({groups[role].length})</h3>
          <div className="bg-white border border-[#111] divide-y divide-[#e5e5e5]">
            {groups[role].map((u) => <UserRow key={u.id} u={u} />)}
          </div>
        </div>
      ))}

      <div className="mb-8">
        <h3 className="font-heading text-lg font-bold tracking-tight mb-3">Writers ({groups.writer.length})</h3>
        {[...TEAMS, null].map((team) => {
          const list = groups.writer.filter((u) => (team ? u.team === team : !u.team));
          if (team === null && list.length === 0) return null;
          return (
            <div key={team || "unassigned"} className="mb-4">
              <div className="flex items-center gap-2 mb-2">
                <Badge className={`rounded-none ${team ? teamColor[team] : "bg-[#999] text-white"}`}>{team || "Unassigned"}</Badge>
                <span className="font-mono-data text-xs text-[#555]">{list.length}</span>
              </div>
              <div className="bg-white border border-[#111] divide-y divide-[#e5e5e5]">
                {list.length === 0
                  ? <p className="px-5 py-3 text-xs text-[#999] font-mono">No writers in this team yet.</p>
                  : list.map((u) => <UserRow key={u.id} u={u} />)}
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="rounded-none border-[#111]">
          <DialogHeader><DialogTitle className="font-heading tracking-tight capitalize">Edit {editUser?.role}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label className="text-xs font-mono uppercase">Name</Label>
              <Input className="rounded-none border-[#111] mt-1" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} data-testid="edit-name-input" /></div>
            <div><Label className="text-xs font-mono uppercase">Login Email (ID)</Label>
              <Input type="email" className="rounded-none border-[#111] mt-1" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} data-testid="edit-email-input" /></div>
            <div><Label className="text-xs font-mono uppercase">New Password (leave blank to keep)</Label>
              <Input className="rounded-none border-[#111] mt-1" placeholder="••••••••" value={editForm.password} onChange={(e) => setEditForm({ ...editForm, password: e.target.value })} data-testid="edit-password-input" /></div>
            {editUser?.role === "writer" && (
              <div><Label className="text-xs font-mono uppercase">Team</Label>
                <Select value={editForm.team || ""} onValueChange={(v) => setEditForm({ ...editForm, team: v })}>
                  <SelectTrigger className="rounded-none border-[#111] mt-1" data-testid="edit-team-select"><SelectValue placeholder="Assign team" /></SelectTrigger>
                  <SelectContent className="rounded-none">{TEAMS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select></div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={saveEdit} className="rounded-none bg-[#0047FF] hover:bg-[#0035c4] text-white" data-testid="save-edit-user-button">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
